import { type AuthToken } from '@reliance/types/models/Auth';
import { EventEmitter2 } from 'eventemitter2';
import resources from '../resources';
import config from '../services/config';
import { store } from '../store/store';

let instance: ConnectServer | null = null;

type ConnectServerCallback = (
  connected: boolean,
  socket: ConnectServer,
) => void;

type ConnectServerMessage =
  | string
  | {
      route: string;
      payload?: unknown;
    };

class ConnectServer extends EventEmitter2 {
  isConnected: boolean = false;
  socket: WebSocket | null = null;
  connectionListeners: ConnectServerCallback[] = [];

  constructor() {
    super();
    this.handleDisconnect = this.handleDisconnect.bind(this);
    this.connectToConnectServer = this.connectToConnectServer.bind(this);
    this.addConnectionListener = this.addConnectionListener.bind(this);
    this.handleConnect = this.handleConnect.bind(this);
    this.handleError = this.handleError.bind(this);
  }

  // Singleton instance getter
  static get(): ConnectServer {
    if (instance == null) {
      instance = new this();
      instance.init();
    }

    return instance;
  }

  // Initialize the line monitor
  init() {
    this.isConnected = false;

    this.connectionListeners = [];
    this.socket = null;

    setTimeout(() => {
      this.connectToConnectServer();
    }, 500);

    return this;
  }

  // Handle a disconnection from the node server
  handleDisconnect() {
    console.log(
      'ConnectServer: Connection to server failed or disconnected. Attempting reconnect',
    );
    for (const callback of this.connectionListeners) {
      callback(false, this);
    }
    this.isConnected = false;
  }

  // Attempt to connect to connect server
  async connectToConnectServer(forceReconnect: boolean = false) {
    if (config.connectServer == null) {
      console.log('ConnectServer: disabled');
      return this;
    }
    if (forceReconnect) {
      this.socket?.close();
      this.socket = null;
    } else if (this.socket) {
      return this;
    }

    let callback: ConnectServerCallback;

    const appState = store.getState();

    this.socket = new WebSocket(config.connectServer.url as string);

    this.socket.addEventListener('error', (err) => {
      console.log(`ConnectServer: Socket.IO error: `, err);
      if (!this.isConnected) {
        this.handleDisconnect();
      }
    });
    this.on('authentication.success', () => {
      console.log('ConnectServer: Connected to server');
      for (callback of this.connectionListeners) {
        callback(true, this);
      }
      this.isConnected = true;
    });

    this.socket.addEventListener('message', (event) => {
      const message = JSON.parse(event.data);
      this.emit(message.resource.replace('/', '.'), message.payload || {});
    });

    if (appState.auth.loggedIn) {
      this.authenticate((await resources.getToken()) as AuthToken);
    }

    return this.socket;
  }

  // Add a listener for connection status changes
  addConnectionListener(callback: ConnectServerCallback) {
    if (this.isConnected) {
      callback(true, this);
    }

    this.connectionListeners.push(callback);
    return this;
  }

  // Remove a listener for connection status changes
  removeConnectionListener(callback: ConnectServerCallback) {
    this.connectionListeners.splice(
      this.connectionListeners.indexOf(callback),
      1,
    );
    return this;
  }

  // Handle the socket connection
  handleConnect() {
    return this;
  }

  // Handle the socket error
  handleError() {
    return this;
  }

  send(message: ConnectServerMessage) {
    const payload =
      typeof message === 'string' ? message : JSON.stringify(message);
    this.waitForConnection(() => {
      this.socket?.send(payload);
    }, 1000);
  }

  waitForConnection = (callback: () => void, interval: number) => {
    if (!config.connectServer || !config.connectServer.url) {
      return;
    }
    if (this.socket?.readyState === WebSocket.OPEN) {
      callback();
    } else {
      if (this.socket?.readyState === WebSocket.CLOSED) {
        this.connectToConnectServer(true);
      }
      setTimeout(() => {
        this.waitForConnection(callback, interval);
      }, interval);
    }
  };

  authenticate(accessToken: AuthToken) {
    const token = accessToken.accessToken;
    if (!this.socket) {
      this.connectToConnectServer();
    } else {
      this.send({
        route: 'web/authenticate',
        payload: { token },
      });
    }
  }

  disconnect() {
    if (this.socket) {
      this.socket.close();
    }
  }
}

export default ConnectServer;
